import {
	AlertType,
	DataType,
	DocumentOrigin,
	DocumentType,
	FieldGroup,
	SubmissionState,
	SubmissionType,
	SubmissionTypeDisplay,
	SubmissionVisisbility,
	TemplateStatus,
	TemplateType,
} from "./enums";

export interface IAction {
	type: string;
	payload: any;
}

export interface IAdminAction {
	type: number;
	payload: any;
}

export interface IAdminCardCountsState {
	adminCardCounts: IAdminOverview;
	isLoading: boolean;
	error?: any;
}

export interface IDeficiencyCount {
	response: number;
	support: number;
	workbook: number;
}

export interface IAdminOverview {
	unApproved: number;
	approved: number;
	cases: number;
	institutions: number;
}

export interface IStringValue {
	key: string;
	value: string;
	stringTypeId: number;
}

export interface IAlert {
	hidden: boolean;
	severity: AlertType;
	message: string;
}

export interface IApproveTemplateCriteria {
	templateId: number;
	updatedByUserId: number;
}

export interface IApproveTemplateResponse {
	success: boolean;
	message: string;
}

export interface IApproveUserCriteria {
	registrationId: number;
	isCreateNewInstitution: boolean;
	insertedBy: number;
	associateInstitutionId: number;
}

export interface IAvailableWorksheetColumnsProps {
	incomingTemplate: Template;
	lastSavedTemplate: Template;
	currentWorksheetId: number;
	onDirtyChange: Function;
	onLoadingChange?: (isLoading: boolean) => void;
}


export interface IBatchApprovalOptionsCriteria {
	sendNotices: boolean;
	sendNoticesAutomatically: boolean;
	caseId: number;
	userId: number;
	mailingBatchId: number;
	notes: string;
}

export interface IBatchApprovalOptionsResponse {
	success: boolean;
	message: string;
}

export interface IApproveUserResponse {
	success: boolean;
	message: string;
}

export interface IAssociateUserCriteria {
	registrationId: number;
}

export interface IAssociateUserResponse {
	success: boolean;
	message: string;
}

export interface ICase {
	updatedByUserId?: number;
	classPeriodTo?: Date | null;
	classPeriodFrom?: Date | null;
	cmxCaseId: string;
	distributionAmount: number;
	distributionDate: Date;
	exclusionDeadline: Date;
	fairnessHearingDate: Date;
	id: number;
	initialMailingDate: Date;
	initialMailingQuantity: number;
	insertedByUserId: string;
	insertedOn: Date;
	name: string;
	number: string;
	objectionDeadline: Date;
	preliminaryHearingDate: Date;
	publicationDeadline: Date;
	casebookCaseDetailId: number;
	isAcceptingSubmissions: boolean;
	isAcceptingMailings: boolean;
	isAcceptingDeficiencyResponse: boolean;
	automaticallySendDeficiencyEmail: boolean;
	submissionAcceptanceUserId?: number;
	claimsDeadline?: Date | null;
	caseStatus: string;
	phoneNumber: string;
	zipCode: string;
	pobox: string;
	courtName: string;
	url: string;
	caseEmail: string;
	courtCaption: string;
	settlementAmountBenefit: string;
	currentTemplateId?: number;
	projectManagerName: string;
	projectManagerEmail: string;
	notApplicableUnknown: boolean;
	totalBenefitsAmount?: number | null;
	detailedBenefitsDescription: string;
	currencyType: string;
	showCmxClaimResults?: boolean;
	offshoreWorkLevel?: number;
	businessAnalysts: string[];
}

export interface ICaseBookDetails {
	caseDetailId: number;
	classPeriodFrom?: Date | null;
	classPeriodTo?: Date | null;
	claimsDeadline?: Date | null;
	caseStatus: string;
	phoneNumber: string;
	zipCode: string;
	pobox: string;
	courtName: string;
	url: string;
	caseEmail: string;
	courtCaption: string;
	settlementAmountBenefit: string;
	ProjectManagerName: string;
	ProjectManagerEmail: string;
}

export interface ICasebookState {
	isLoading: boolean;
	selectedCasebookCaseDetail?: ICaseBookDetails;
	searchResults: ICasebookSearchResults[];
	error?: any;
}

export interface ICasebookSearchResults {
	caseDetailId: number;
	billingCode: string;
	sapContractCode: string;
	caseName: string;
	hasBeenImported: boolean;
}

export interface ICaseCriteria {
	caseNumber?: string;
	caseName?: string;
	classPeriodFrom?: string;
	classPeriodTo?: string;
	claimsDeadline?: string;
	isAcceptingSubmissions?: boolean;
	isOffshoreUser?: boolean;
	hasClaimsDeadline?: boolean;
	omniSearchString?: string;
	pageNumber?: number;
	pageSize?: number;
}

export interface ICaseDocumentType {
	id: number;
	caseId: number;
	documentTypeId: number;
	label: string;
	IsRequiredOriginalOrReplacement: boolean;
	IsRequiredUpdateOrCorrection: boolean;
	default: boolean;
	isConsolidated: boolean;
}

export interface ICaseDocumentTypeCriteria {
	caseId: number;
	submissionType: SubmissionType;
	includeConsolidatedDocumentsType: boolean;
}

export interface ICaseState {
	cases: ICase[];
	totalCount: number;
	isLoading: boolean;
	isDownloading: boolean;
	selectedCaseDetail?: ICase;
	selectedCaseTemplate?: ITemplate;
	selectedCaseInstitutions?: IInstitution[];
	error: any;
	claimFileUploadResponse?: IClaimFileUploadResponse;
	saveSuccess: boolean;
	needsRefresh: boolean;
}

export interface ICaseSubmission {
	submissionId: number;
	caseId: number;
	submissionState: string;
	submissionDate: Date;
	documentName: string;
}

export interface ICaseSubmissionCriteria {
	submissionDate?: string;
	submissionId?: string;
	documentName?: string;
	submissionState?: string;
}

export interface ICaseTemplate {
	caseId: number;
	templateId: number;
	templateDocumentId?: number;
	deficiencyTemplateDocumentId?: number;
}

export interface IClaimantCriteria {
	submissionId: number;
	accountTypes: string[];
	accountNumber: string;
	keyName: string;
	cmxClaimNumber?: number;
	cmxCaseNumber?: number;
	pageNumber: number;
	rowsPerPage: number;
	sortField?: string;
	sortDirection?: string;
	claimStatusList: string[];
}

export interface IClaimantListItem {
	id: number;
	submissionId: number;
	accountType: string;
	accountNumber: number;
	keyName: string;
	cmxClaimNumber?: number;
	trackingNumber: number;
	claimStatus: string;
}

export interface IClaimantListResponse {
	claimants: IClaimantListItem[];
	totalRowCount: number;
}

export interface IClaimantListState {
	claimants: IClaimantListItem[];
	totalRowCount: number;
	pageNumber: number;
	rowsPerPage: number;
	isLoaded: boolean;
	isLoading: boolean;
	error: any;
}

export interface IClaimFileModalProps {
	handleClose: Function;
	submissionTypeDisplay: SubmissionTypeDisplay;
	submissionType: SubmissionType;
	parentSubmissionId: number;
	caseId: number;
	caseName: string;
	caseNumber: string;
	handleSupportingDocModalOpen: Function;
	handleSupportingDocModalClose: Function;
	setSupportingDocModalProps: Function;
	isNonClaimDeficiencyResponse: boolean;
	submittedOnBehalfOfUserId?: number;
	submittedOnBehalfOfInstitutionId?: number;
	submittedOnBehalfOfUserEmail?: string;
}

export interface IClaimFileState {
	isUploading: boolean;
	isUploadFailed: boolean;
	uploadResponse?: IClaimFileUploadResponse;
	publishMessage: boolean;
	publishMessageSuccess: boolean;
	publishMessageDispatched: boolean;
	deleteSubmissionResponse?: IResponse;
	loadingDocumentTypes: boolean;
	loadDocumentTypesSuccess: boolean;
	documentTypes?: ICaseDocumentType[];
	supportEmailAddress?: IConfiguration;
	deleteDocumentResponse?: IResponse;
	loadClaimAcknowledgementTextDispatched: boolean;
	claimAcknowledgementText?: IConfiguration;
	supportingDocTypeProps: ISupportingDocTypeProps[];
	isDeleted: boolean;
}

export interface IClaimFileUploadResponse {
	success: boolean;
	message: string;
	submissionId?: number;
	submissionDescription?: string;
}

export interface IConfiguration {
	id: number;
	key: string;
	value: string;
}

export interface IDeleteTemplateResponse {
	success: boolean;
	message: string;
}

export interface IDeleteUser {
	userId: number;
	deletedByUserId: number;
}

export interface IDenyTemplateCriteria {
	templateId: number;
	updatedByUserId: number;
}

export interface IDenyTemplateResponse {
	success: boolean;
	message: string;
}

export interface IDenyUserCriteria {
	userId: number;
}

export interface IDenyUserResponse {
	success: boolean;
	message: string;
}

export interface IDocument {
	id: number;
	name: string;
	documentType: DocumentType;
	storageId: string;
	insertedOn: Date;
	insertedByUserId: number;
	documentOrigin: DocumentOrigin;
}

export interface IDocumentation {
	id: number;
	documentType: DocumentType;
	name: string;
	submittedOn: Date;
	documentOrigin: DocumentOrigin;
	isMailingBatchSent: boolean;
	mailingBatchId: number;
	submissionNumber: string;
	isApproved: boolean;
}

export interface IDocumentListCriteria {
	fileName?: string;
	submittedOn?: string;
	docTypeList?: number[];
	includeRelatedSubmissions?: boolean;
}

export interface IDrawerMenu {
	DrawerMenuItems: IDrawerMenuItem[];
	SecondaryMenuItems: IDrawerMenuItem[];
}

export interface IDrawerMenuItem {
	DisplayText: string;
	Route: string;
	IconName: string;
	Tooltip: string;
}

export interface IEnumDTO {
	label: string;
	value: string; //string representation of an int, to ease display pain
}

export interface IFieldDTO {
	id: number;
	isEnabled: boolean;	//this doesn't exist in the db. it will be set in UI code.
	isRequired: boolean;	
	fieldGroupId: number;
	name: string;
	customName: string; //this also doesn't exist in the db.
	label: string;
	dataTypeId: DataType;
}
export interface IFilterDTO {
	parameter: string; // the parameter name in the stored proc that accesses the data
	type: string; // the data type
	value: string; // the value to filter on
}

export interface IImportCase {
	id: number;
	cmxCaseId?: number;
	federalCaseNumber: string;
	federalCaseName: string;
	cmxCaseNumber: string;
	cmxCaseName: string;
	insertedByUserId: number;
	casebookCaseDetailId: number;
	sapContractCode?: number;
	projectManagerName: string;
	projectManagerEmail: string;
}

export interface IInstitution {
	id: number;
	name: string;
	institutionTypeName: string;
	institutionTypeId: number;
	phone: string;
	address1: string;
	address2: string;
	address3: string;
	city: string;
	stateProvince: string | null;
	zipCode: string;
	country: string;
	insertedOn: Date;
	insertedByUserId: number;
	insertedByUserName: string;
	updatedOn: Date;
	updatedByUserId: number;
	normalizedAddress: string;
	institutionUserEmail: string;
	institutionUserId: number;
}

export interface IInstitutionCriteria {
	id?: number;
	email?: string;
	name?: string;
	type?: string;
	phone?: string;
	address?: string;
}

export interface IInstitutionListState {
	institutionList: IInstitution[];
	isLoading: boolean;
	error?: any;
	errorGetInstitutionType?: any;
	errorGetCountriesStates?: any;
	errorGetInstitutionById?: any;
	errorUpdateInstitution?: any;
	slideoutDrawer?: any;
	updateInstitutionResponse?: any;
	updateSuccess?: any;
}

export interface IInstitutionType {
	key: string;
	value: number;
}

export interface IInstitutionUpdate {
	institution: IInstitution;
	isLoading: boolean;
	selectedInstitution?: IInstitution;
}

export interface IManageUserRolesResponse {
	success: boolean;
	message: string;
}

export interface INomineeCard {
	started: number;
	submitted: number;
	verifying: number;
	verified: number;
	extracting: number;
	extracted: number;
	loading: number;
	loaded: number;
	partiallyExtracted: number;
	partiallyLoaded: number;
	error: number;
	rejected: number;
	deficient: number;
}

export interface INomineeCardCountsState {
	cardCounts: INomineeCard;
	isLoading: boolean;
	error?: any;
}

export interface IRegisterUser {
	id: number;
	email: string;
	lastLogin: Date;
	insertedOn: Date;
	institution: IInstitution;
	firstName: string;
	lastName: string;
	phone: number;
	extension: number;
	isActive: boolean;
	isApproved: boolean;
	registrationId: number;
	approvedOn: Date;
	confirmedOn: Date;
	approvedByUserId: number;
	approvedByUserName: string;
	registrationInstitutionName: string;
	confirmedOnStartDate?: string;
	confirmedOnEndDate?: string;
	registration: IRegistration;
	roles: string[];
	isEpiqUser: boolean;
    isInstitutionUser: boolean;	
}

export interface IRegistration {
	id: number;
	userId: number;
	institutionName: string;
	institutionTypeId: number;
	phone: string;
	userEmail: string;
	institutionEmail: string;
	address1: string;
	address2: string;
	address3: string;
	city: string;
	stateProvince: string;
	zipCode: string;
	country: string;
	insertedOn: Date;
}

export interface IRegistrationDetail {
	id: number;
	userId: string;
	institutionName: string;
	institutionTypeId: number;
	phone: string;
	institutionEmail: string;
	userEmail: string;
	address1: string;
	address2: string;
	address3: string;
	city: string;
	stateProvince: string;
	zipCode: string;
	country: string;
	user: IRegisterUser;
}

export interface IRemoveUserAccessResponse {
	success: boolean;
	message: string;
}

export interface IResponse {
	success: boolean;
	message: string;
}

export interface IReviewUserCriteria {
	registrationId: number;
	history: any;
}

export interface IReviewUserResponse {
	success: boolean;
	message: string;
}

export interface IRoles {
	id: string;
	name: string;
	isEpiq: boolean;
	isEpiqDefault: boolean;
	isNomineeDefault: boolean;
	isChecked: boolean;
	disabled: boolean;
}

export interface IRolesState {
	roles: IRoles[];
	isLoading: boolean;
	error?: any;
}

export interface ISaveTemplateResponse {
	success: boolean;
	message: string;
}

export interface ISubmission {
	id: number;
	number: string;
	caseName: string;
	status: string;
	dateOfSubmission: Date;
	fileName: string;
	institutionName: string;
}

export interface ISubmissionCriteria {
	dateOfSubmissionEnd?: string;
	dateOfSubmissionStart?: string;
	number?: string;
	fileName?: string;
	caseName?: string;
	institutionName?: string;
	institutionId?: number;
	statusList?: number[];
	userId: number;
	isUserSubmissions: boolean;
	pageNumber: number;
	rowsPerPage: number;
	sortField?: string;
	sortDirection?: string;
	isOffshoreUser?: boolean;
}

export interface ISubmissionDetail {
	id: number;
	caseId: number;
	submittedByUserName: string;
	submittedByUserId: number;
	submissionOriginId: number;
	fileSubmittedByUserName: string;
	institutionName: string;
	institutionId: number;
	submissionDescription: string;
	caseName: string;
	caseNumber: string;
	submissionLastModDate: Date;
	summaryCompanyName: string;
	summaryCompanyAddress1: string;
	summaryCompanyAddress2: string;
	summaryCompanyCity: string;
	summaryCompanyState: string;
	summaryCompanyZipCode: string;
	summaryCompanyCountry: string;
	summaryContactName: string;
	summaryContactPhone: string;
	summaryContactEmail: string;
	summaryTotalClaimants: number;
	summaryTotalTransactions: number;
	submissionStatus: string;
	loadedClaimantCount: number;
	loadedTransactionCount: number;
	parentSubmissionId: number;
	documentSubmissions: IDocumentation[];
	cmxCACaseNumber: number;
	receivedOn: Date;
	postMarkedOn: Date;
	transitionedOn: Date;
	beneficialOwnerCount: number;
	transactionCount: number;
	statedClaimantCount: number;
	statedTransactionCount: number;
	newestRelatedSubmissionId?: number;
	newestRelatedSubmissionTypeId?: SubmissionType;
	showCmxClaimResults: boolean;
	isCaseAcceptingSubmissions: boolean;
	isCaseAcceptingDeficiencyResponse: boolean;
	canAcceptDeficiencyResponse: boolean;
	submissionTypeId: SubmissionType;
	isNonClaimDeficiencyResponse: boolean;
	submissionStateId: SubmissionState;
	submittedByEpiqUserId?: number;
	submittedByEpiqUserEmail?: string;
	submittedByUserEmail: string;
}

export interface ISubmissionListItem {
	id: number;
	submissionDescription: string;
	caseId: number;
	caseName: string;
	dateOfSubmission: Date;
	latestSubmissionId: number;
	fileName: string;
	institutionName: string;
	status: string;
	submissionStateId: SubmissionState;
	hasRelatedSubmissions: boolean;
	caseNumber: string;
	parentSubmissionId: number;
	submissionTypeId: SubmissionType;
	institutionId: number;
	relatedSubmissions?: ISubmissionListItem[];
	submissionVisibilityId: SubmissionVisisbility;
	submittedByUserId: number;
	submittedByUserName: string;
	canAcceptDeficiencyResponse: boolean;
	isNonClaimDeficiencyResponse: boolean;
	submittedByEpiqUserId?: number;
	submittedByUserEmail: string;
}

export interface ISubmissionListResponse {
	submissions: ISubmissionListItem[];
	totalRowCount: number;
}

export interface ISubmissionListState {
	submissions: ISubmissionListItem[];
	mailingSubmissions: ISubmissionListItem[];
	pageNumber: number;
	totalRowCount: number;
	rowsPerPage: number;
	isLoaded: boolean;
	isLoading: boolean;
	isDownloading: boolean;
	error: any;
	errorGetRelatedSubmissions: any;
	errorGetDocuments: any;
	errorGetSubmissionDocument: any;
	institutionDetail: IInstitution;
	submissionDocuments: IDocument[];
	relatedSubmissions: ISubmissionListItem[];
	documentsLoading: boolean;
	documentsLoaded: boolean;
	showUserSubmissions: boolean;
	deficientTemplateGenerating: boolean;
	batchApprovalOptionsResponse: boolean;
	isBatchApprovalProcessLoading: boolean;
	mailingSubmissionListNeedsRefresh: boolean;
}

export interface ISubmissionRejection {
	id: number;
	submissionId: number;
	rejectionTypeId: number;
	message: string;
	count: number;
	sourceLocation: string;
	sourceLocationSortOrder: number;
}

export interface ISubmissionRejectionSlideoutProps {
	submissionDocumentId: number;
	submissionId: number;
	submissionFileName: string;
	handleClose: any;
}

export interface ISupportingDocModalProps {
	handleClose: Function;
	submissionTypeDisplay: SubmissionTypeDisplay;
	submissionType: SubmissionType;
	submissionId: number;
	caseId: number;
	caseName: string;
	caseNumber: string;
	submissionState: SubmissionState;
	submissionDescription: string;
}

export interface ISupportingDocTypeProps {
	id: number;
	docTypeDescription: string;
	existingFileName: string;
	existingDocumentId: number;
	isUploading: boolean;
	isUploaded: boolean;
	isUploadFailed: boolean;
	isNotProvided: boolean;
	progress: number;
	file?: File;
	fileIsCorrectType: any;
	response: IResponse;
	tooltipText?: string;
	isConsolidatedDocumentsType: boolean;
}

export interface ISystemData {
	templateId: number;
	caseId: number;
	institutionId: number;
	requestingUserId: number;
	storageId: string;
	submissionId: number;
	submissionTypeId: number;
	submissionMethod: number;
}

export interface ITemplate {
	id: number;
	name: string;
	templateType: TemplateType;
	version: number;
	insertedOn: Date;
	insertedByUserId: number;
	updatedOn: Date;
	updatedByUserId: number;
	updatedByUser: string;
	documentId: number;
	cases: number;
	description: string;
	caseCount: number;
	instructions: string;
	worksheets: IWorksheet[];
	caseList: ICase[];
	templateStatusId: TemplateStatus;
}

export interface ITemplateCriteria {
	name?: string,
	statusText?: string,
	templateType?: number[],
	statusList?: number[]
}

export interface ITemplateState {
	templateStatusId: TemplateStatus;
	templates: ITemplate[];
	isLoading: boolean;
	isDownloading: boolean;
	selectedTemplate?: ITemplate;
	templateDetail?: ITemplate;
	saveSuccess?: boolean;
	approveTemplateResponse?: IApproveTemplateResponse;
	denyTemplateResponse?: IDenyTemplateResponse;
	error: any;
	needsRefresh: boolean;
}

export interface IUpdateInstitutionResponse {
	success: boolean;
	message: string;
}

export interface IUpdateUserResponse {
	success: boolean;
	message: string;
}

export interface IUserCriteria {
	id?: number;
	email?: string;
	firstName?: string;
	lastName?: string;
	institutionName?: string;
	approvedOnStartDate?: string;
	approvedOnEndDate?: string;
	isApproved?: boolean | null;
	confirmedOnStartDate?: string;
	confirmedOnEndDate?: string;
	isConfirmed?: boolean | null;
    includeInstitutionUsers?: boolean | null;
}

export interface IUserListState {
	needsRefresh: boolean;
	userList: IRegisterUser[];
	rolesList: IRoles[];
	isLoading: boolean;
	error?: any;
	institution?: IInstitution;
	registrationDetail?: IRegisterUser;
	approveUserResponse?: any;
	denyUserResponse?: any;
	slideoutDrawer?: any;
	updateUserResponse?: any;
	removeUserAccess?: any;
}
export interface IUser {
    id: number;
	name: string;
	institutionId: number;
	isInstitutionUser: boolean;	
	institution?: IInstitution;
	roles?: string[];
}

export interface IUserState {
	user: IUser;
	isLoading: boolean;
	error?: any;
}

export interface ICmxClaimantStatus {
	trackingNumber: number;
	status: string;
}

export interface ICmxTrackingNumberList {
	caseNumber: number;
	trackingNumbers: number[];
}

export interface ICmxState {
	statusLoading: boolean;
	statusLoaded: boolean;
	cmxClaimantStatusList?: ICmxClaimantStatus[];
	error?: any;
}

export interface IClaimCountsDTO {
	accepted: number;
	underReview: number;
	deficient: number;
	withdrawn: number;
	duplicate: number;
	denied: number;
	paid: number;
}

export interface Template {
	id: number;
	name: string;
	description: string;
	worksheets: IWorksheet[];
}

export interface ITemplateWorksheetProps {
	incomingTemplate: Template;
	lastSavedTemplate: Template;
	onDirtyChange: Function;
	handleOpenNextDialog: Function;
}

export interface ITemplateWorksheetColumnsProps {
	incomingTemplate: Template;
    lastSavedTemplate: Template;
	currentWorksheetId: number;
	onDirtyChange: Function;
	handleOpenThirdDialog: Function;
}

export interface IWorksheet {
	id: number;
	isEnabled: boolean;
	fieldGroup: FieldGroup;
	name: string;
	number: number;
	customName: string | null;
	codeName: string;
	isRequired: boolean;
	worksheetColumns?: IWorksheetColumn[];
}

export interface IWorksheetColumn {
	id: number;
	customName: string | null;
	name: string;
	mapToField: string;
	isRequired: boolean;
	number: number;
	fieldIsRequired: boolean;
	fieldName: string;
	mapToFieldId: number
	worksheetId: number;
	order: number;
	insertedByUserId: number;
	validationInfo?: string | null;
	note?: string | null;
	dataTypeId?: DataType;
	className?: string | null;
}

export enum RejectType {
	WrongTemplate = 1,
	InvalidTabs = 2,
	InvalidColumns = 3,
	NoValidClaimants = 4,
	TransactionThresholdExceeded = 5,
	InvalidFileStructure = 6,
	InvalidSummary = 7,
	InvalidBooleanValue = 8,
	InvalidCusip = 9,
	InvalidExerciseAssignDate = 10,
	MissingExerciseAssignDate = 11,
	InvalidExpirationDate = 12,
	MissingExpirationDate = 13,
	InvalidOptionDetail = 14,
	MissingOptionDetail = 15,
	InvalidAccountNumber = 16,
	MissingAccountNumber = 17,
	InvalidTransactionGroup = 18,
	InvalidTransactionTypePrice = 19,
	NewTransactionsInDeficientWorksheet = 20,
	InvalidStrikePrice = 21,
	MissingStrikePrice = 22,
	InvalidSecurityType = 23,
	InvalidTransactionType = 24,
	MissingTransactionType = 25,
	MissingPrice = 26,
	InvalidTransactionDate = 27,
	MissingTransactionDate = 28,
	MissingQuantity = 29,
	MissingCustomDataField = 30,
	InvalidSecurityTypeCustomMessage = 31,
	InvalidTransactionTypeCustomMessage = 32,
	InvalidCurrencyTypeCustomMessage = 33,
	InvalidCusipCustomMessage = 34,
	InvalidTransactionDateCustomMessage = 35,
	ClaimantHasTransactionErrors = 36,
	NewClaimantOnDeficientWorksheet = 37,
	ClaimantInvalidAccountTypeCustomMessage = 38,
	ClaimantBeneOwnerFirstNameInvalidLength = 39,
	ClaimantBeneOwnerLastNameInvalidLength = 40,
	ClaimantJointOwnerFirstNameInvalidLength = 41,
	ClaimantJointOwnerLastNameInvalidLength = 42,
	ClaimantBusinessNameInvalidLength = 43,
	ClaimantRepresentativeNameInvalidLength = 44,
	ClaimantInvalidName = 45,
	ClaimantMissingName = 46,
	ClaimantInvalidJointOwnerBusinessName = 47,
	ClaimantInvalidJointOwnerName = 48,
	ClaimantInvalidRepresentativeName = 49,
	ClaimantMissingRepresentativeName = 50,
	ClaimantMissingJointOwnerName = 51,
	ClaimantInvalidBeneficialOwnerName = 52,
	ClaimantMissingCountry = 53,
	ClaimantCountryInvalidLength = 54,
	ClaimantInvalidCountry = 55,
	ClaimantMissingState = 56,
	ClaimantInvalidStateOrProvince = 57,
	ClaimantMissingZipCode = 59,
	ClaimantInvalidZipCodeOrPostalCode = 60,
	ClaimantStateProvinceNotAllowed = 62,
	ClaimantMissingAddress = 63,
	ClaimantAddressInvalidLength = 64,
	ClaimantMissingCity = 65,
	ClaimantCityInvalidLength = 66,
	ClaimantMissingAccountNumber = 67,
	ClaimantInvalidAccountNumberLength = 68,
	ClaimantDuplicateAccountNumber = 69,
	MissingWorksheetCustomMessage = 70,
	InvalidWorksheetCustomMessage = 71,
	MissingColumnCustomMessage = 72,
	InvalidColumnCustomMessage = 73,
	InvalidMoneyRange = 158,
	MissingRequiredField = 159,
	InvalidDateRange = 160,
	InvalidCurrency = 161,
	InvalidTicker = 162,
	InvalidAccountType = 163,
	MailingFileInvalidAddressLength = 164,
	MailingFileInvalidNameLength = 165,
	MailingFileHasExtraColumn = 166,
	InvalidExtraColumns = 167,
	NoValidRows = 168,
	MailingFileInvalidColumnLength = 169,
	MailingFileErrorThresholdExceeded = 170,
	FileReadError = 171
}
